// How the tutorial performs (#1848 ticket 10), as Yandex Metrika goals: the
// offer's answers, each unit's start, steps, end — and the step it was left
// on. Sent from the runner and the offer card only, never from a step, so a
// chapter landed later is covered with no analytics code of its own.
//
// `ym` is the counter `index.html` loads. Without it (dev with a blocker,
// tests) every event is a no-op, and a throwing counter never reaches the app.

import type { TutorialRun } from '@/atoms/tutorial';
import type { RunOutcome, TutorialUnit } from '@/types';
import type { OfferSurface } from './tutorialOffer';

/** The counter `index.html` initialises — its snippet hardcodes the same id,
 *  so change both. Webvisor stays off there on purpose: it would record
 *  screenplay text. */
export const METRIKA_COUNTER_ID = 113864009;

/** Which card the offer events come from: the first offer, or the resume form. */
export type OfferForm = 'offer' | 'resume';

/** A tutorial event; its `name` is the Metrika goal, `tutorial_`-prefixed. Steps are 0-based. */
export type TutorialEvent =
    /** `surface`: the Editor, the projects list, or the screen landed on after sign-up or login. */
    | { name: 'offer_shown' | 'offer_started'; unit: TutorialUnit; form: OfferForm; surface: OfferSurface }
    | { name: 'offer_snoozed' | 'offer_dismissed'; unit: TutorialUnit; form: 'offer'; surface: OfferSurface }
    | { name: 'unit_started'; unit: TutorialUnit; resumed: boolean }
    | { name: 'step_entered' | 'step_completed'; unit: TutorialUnit; step: number }
    | { name: 'unit_completed'; unit: TutorialUnit }
    /** `step` is `null` when no step was on screen (the setup, the resume form). */
    | { name: 'unit_exited'; unit: TutorialUnit; step: number | null }
    /** From the card the run ended on: `done` is the closing card, `skipped` the exit prompt. */
    | { name: 'project_deleted'; unit: TutorialUnit; outcome: RunOutcome };

export function trackTutorial({ name, ...params }: TutorialEvent) {
    try {
        window.ym?.(METRIKA_COUNTER_ID, 'reachGoal', `tutorial_${name}`, params);
    } catch {
        // Analytics never breaks the tutorial.
    }
}

/** The runs whose start was sent: a run is one object from «Начать» (or the
 *  reload that resumes it) to its end, however often the runner remounts. */
const started = new WeakSet<TutorialRun>();

/** A run the runner picked up: sent once, fresh or resumed. */
export function trackRunStarted(run: TutorialRun) {
    if (run.ended || started.has(run)) return;
    started.add(run);
    trackTutorial({ name: 'unit_started', unit: run.unit, resumed: run.resumed === true });
}

/** One walk of a unit's steps: remembers the step on screen, so an exit can
 *  name it, and ends once. */
export function createRunTracker(unit: TutorialUnit) {
    let step: number | null = null;
    let over = false;
    return {
        stepEntered(index: number) {
            if (over) return;
            step = index;
            trackTutorial({ name: 'step_entered', unit, step: index });
        },
        stepCompleted(index: number) {
            if (!over) trackTutorial({ name: 'step_completed', unit, step: index });
        },
        ended(outcome: RunOutcome) {
            if (over) return;
            over = true;
            trackTutorial(outcome === 'done' ? { name: 'unit_completed', unit } : { name: 'unit_exited', unit, step });
        },
    };
}
